const smoothActiveScrollToTop = ({ smooth = true } = {}) => {
  // Strategy copied from the console test that worked:
  // `__scrollTest.smoothActive()`.
  //
  // What it does:
  // 1) Detects the currently active scroll container (the element that can scroll
  //    and currently has scrollTop > 0).
  // 2) Calls smooth scroll on THAT element.
  //
  // Why this works here:
  // - In this app shell, the page may scroll inside a nested container instead of
  //   always using `window` or `document.scrollingElement`.
  // - When we call `window.scrollTo(...)` on the wrong scroller, nothing moves.
  // - Targeting the active scroller guarantees we scroll the element the user is
  //   actually interacting with.
  //
  // Why previous attempts failed:
  // - `window.scrollTo(...)` only affects the window scroll context.
  // - `document.scrollingElement` can be correct in many apps, but not when a
  //   nested overflow container owns the scroll position.
  // - So those calls can appear to "do nothing" even though they execute.
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const behavior = smooth ? "smooth" : "auto";

  const isScrollableElement = (element) => {
    if (!element) {
      return false;
    }

    const style = window.getComputedStyle(element);
    const overflowY = style.overflowY;
    const canScroll = /(auto|scroll|overlay)/.test(overflowY);

    return canScroll && element.scrollHeight > element.clientHeight;
  };

  const allElements = Array.from(document.querySelectorAll("*"));
  const activeScrollableElement = allElements.find((element) => {
    return isScrollableElement(element) && (element.scrollTop || 0) > 0;
  });

  const target =
    activeScrollableElement ||
    document.scrollingElement ||
    document.documentElement ||
    document.body;

  if (target && typeof target.scrollTo === "function") {
    target.scrollTo({ top: 0, left: 0, behavior });
    // The first scrolled element in DOM order isn't always the page scroller
    // (e.g. a nested list that happens to be scrolled). Reset the root scroller
    // too so the page itself always returns to the top.
    const root = document.scrollingElement;
    if (root && root !== target && typeof root.scrollTo === "function") {
      root.scrollTo({ top: 0, left: 0, behavior });
    }
    return;
  }

  window.scrollTo({
    top: 0,
    left: 0,
    behavior,
  });
};

export default smoothActiveScrollToTop;

// Comment from copilot to think about if current implementation is slow:
/*
smoothActiveScrollToTop scans the entire DOM (document.querySelectorAll('*')) on every call to find a scrollable element. 
On large pages this is O(N) and can become noticeably slow, especially when paging quickly. 
Consider a cheaper strategy (e.g., prefer document.scrollingElement, check known app shell containers, 
or walk up from document.activeElement to find the nearest scrollable ancestor).
*/
